import React, { ReactNode } from "react";
import { Col, Container, Row, Spinner, Alert, Button, Form } from "react-bootstrap";
import { Sun, Moon, BookHalf } from "react-bootstrap-icons";
import { getAnalyticsWidget, createExportSchedule } from "../../../utils/Requests";
import { getWidgetTheme } from "../../../utils/analytics";
import { MetricConfig, AnalyticsFilters, DocsSidebarEntry } from "../../../types/analytics";
import { InteractiveLineChart, ExportButton, ScheduleExportModal, DocsSidebar, WidgetCard, PopPeriodBanner } from "../../../components/analytics";
import MetricCards, { MetricCardItem } from "../../../components/analytics/MetricCards";
import { ExportContext, ExportableData, ExportScheduleRequest } from "../../../services/export";
import Logger from "../../../utils/Logger";

/* eslint-disable camelcase */
type WeeklyExecutiveSummaryDataPoint = {
    week_start: string;
    week_end: string;
    visitors: number;
    video_views: number;
};
/* eslint-enable camelcase */

type WeeklyExecutiveSummaryProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type WeeklyExecutiveSummaryFilters = {
    startDate: string;
    endDate: string;
};

type WeeklyExecutiveSummaryState = {
    analyticsData: WeeklyExecutiveSummaryDataPoint[];
    isLoading: boolean;
    error: string | null;
    filters: WeeklyExecutiveSummaryFilters;
    appliedFilters: WeeklyExecutiveSummaryFilters;
    isDarkTheme: boolean;
    filterKey: number;
    isScrolled: boolean;
    selectedPreset: string;
    showScheduleModal: boolean;
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
};

const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

class WeeklyExecutiveSummary extends React.Component<WeeklyExecutiveSummaryProps, WeeklyExecutiveSummaryState> {
    private containerRef = React.createRef<HTMLDivElement>();

    constructor(props: WeeklyExecutiveSummaryProps) {
        super(props);

        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        const threeMonthsAgo = new Date(yesterday);
        threeMonthsAgo.setMonth(yesterday.getMonth() - 3);

        const initialFilters: WeeklyExecutiveSummaryFilters = {
            startDate: formatDate(threeMonthsAgo),
            endDate: formatDate(yesterday)
        };

        this.state = {
            analyticsData: [],
            isLoading: true,
            error: null,
            filters: initialFilters,
            appliedFilters: initialFilters,
            isDarkTheme: true,
            filterKey: 0,
            isScrolled: false,
            selectedPreset: "3M",
            showScheduleModal: false,
            sidebarOpen: false,
            sidebarEntries: []
        };
    }

    componentDidMount(): void {
        this.fetchData();
        window.addEventListener("scroll", this.handleScroll);
    }

    componentWillUnmount(): void {
        window.removeEventListener("scroll", this.handleScroll);
    }

    private handleScroll = (): void => {
        const isScrolled = window.scrollY > 10;
        if (isScrolled !== this.state.isScrolled) {
            this.setState({ isScrolled });
        }
    };

    private handleDatePreset = (preset: string): void => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        let startDate: Date;
        const endDate: Date = yesterday;

        switch (preset) {
            case "4W":
                startDate = new Date(yesterday);
                startDate.setDate(yesterday.getDate() - 27);
                break;
            case "3M":
                startDate = new Date(yesterday);
                startDate.setMonth(yesterday.getMonth() - 3);
                break;
            case "6M":
                startDate = new Date(yesterday);
                startDate.setMonth(yesterday.getMonth() - 6);
                break;
            case "Y":
                startDate = new Date(yesterday);
                startDate.setDate(yesterday.getDate() - 365);
                break;
            default:
                return;
        }

        this.setState(prevState => ({
            filters: {
                ...prevState.filters,
                startDate: formatDate(startDate),
                endDate: formatDate(endDate)
            },
            selectedPreset: preset
        }));
    };

    private fetchData = async (): Promise<void> => {
        try {
            this.setState({ isLoading: true, error: null });

            const { appliedFilters } = this.state;

            const response = await getAnalyticsWidget(
                this.props.customerConfig,
                "weekly_executive_summary",
                appliedFilters.startDate,
                appliedFilters.endDate
            );

            const data = (response as any)?.result;
            const dataArray = Array.isArray(data) ? data : [];
            const meta = (response as any)?.widget;
            /* eslint-disable camelcase */
            const sidebarEntries: DocsSidebarEntry[] = meta ? [{
                widgetKey: "weekly_executive_summary",
                displayName: meta.display_name,
                description: meta.description,
                schema: (response as any)?.schema
            }] : [];
            /* eslint-enable camelcase */
            this.setState({ analyticsData: dataArray, sidebarEntries });
        } catch (err) {
            Logger.error("Error fetching weekly executive summary data", err);
            this.setState({ error: "Failed to fetch weekly executive summary data" });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    private handleDateChange = (field: "startDate" | "endDate", value: string): void => {
        this.setState(prevState => ({
            filters: { ...prevState.filters, [field]: value },
            selectedPreset: ""
        }));
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters },
            filterKey: prevState.filterKey + 1
        }), () => {
            this.fetchData();
        });
    };

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private convertToAnalyticsFilters = (): AnalyticsFilters => {
        const { appliedFilters } = this.state;
        return {
            startDate: appliedFilters.startDate,
            endDate: appliedFilters.endDate,
            attributionId: [],
            brandName: [],
            devicePlatform: []
        };
    };

    private getExportData = (): ExportableData[] => {
        const { analyticsData } = this.state;

        if (analyticsData.length === 0) {
            return [];
        }

        /* eslint-disable camelcase */
        return [{
            title: "Weekly Executive Summary",
            columns: [
                { key: "week_start", label: "Week Start", format: "date" },
                { key: "week_end", label: "Week End", format: "date" },
                { key: "visitors", label: "Visitors", format: "number" },
                { key: "video_views", label: "Video Views", format: "number" }
            ],
            rows: analyticsData,
            metadata: {
                filters: this.convertToAnalyticsFilters(),
                pageKey: "weekly_executive_summary",
                customerSlug: this.props.customerConfig.slug
            }
        }];
        /* eslint-enable camelcase */
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "Weekly Executive Summary",
            pageKey: "weekly_executive_summary",
            customerSlug: this.props.customerConfig.slug,
            filters: this.convertToAnalyticsFilters(),
            getData: this.getExportData,
            getContainerRef: () => this.containerRef.current
        };
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
    };

    private renderSummaryBlocks(): ReactNode {
        const { analyticsData, isDarkTheme, sidebarEntries } = this.state;
        const theme = getWidgetTheme(isDarkTheme);
        const schema = sidebarEntries[0]?.schema as Record<string, { description?: string }> | undefined;

        if (analyticsData.length === 0) {
            return null;
        }

        const totalVisitors = analyticsData.reduce((sum, d) => sum + d.visitors, 0);
        /* eslint-disable camelcase */
        const totalVideoViews = analyticsData.reduce((sum, d) => sum + d.video_views, 0);
        /* eslint-enable camelcase */

        const metrics: MetricCardItem[] = [
            { key: "visitors", label: "Visitors", value: totalVisitors, description: schema?.["visitors"]?.description },
            { key: "video_views", label: "Video Views", value: totalVideoViews, description: schema?.["video_views"]?.description }
        ];

        return (
            <WidgetCard theme={theme}>
                <MetricCards metrics={metrics} theme={theme} />
            </WidgetCard>
        );
    }

    private renderChart(): ReactNode {
        const { analyticsData, isDarkTheme } = this.state;
        const theme = getWidgetTheme(isDarkTheme);

        const metricConfig: MetricConfig[] = [
            { key: "visitors", name: "Visitors", color: "#8884d8" },
            { key: "video_views", name: "Video Views", color: "#82ca9d" }
        ];

        /* eslint-disable camelcase */
        const initialVisibleMetrics: Record<string, boolean> = {
            visitors: true,
            video_views: true
        };
        /* eslint-enable camelcase */

        return (
            <WidgetCard
                title="Weekly Trends"
                description={this.state.sidebarEntries[0]?.description}
                theme={theme}
                isEmpty={analyticsData.length === 0}
            >
                <InteractiveLineChart
                    title=""
                    data={analyticsData}
                    xAxisDataKey="week_start"
                    metrics={metricConfig}
                    initialVisibleMetrics={initialVisibleMetrics}
                    theme={theme}
                />
            </WidgetCard>
        );
    }

    private hasChanges = (): boolean => {
        const { filters, appliedFilters } = this.state;
        return (
            filters.startDate !== appliedFilters.startDate ||
            filters.endDate !== appliedFilters.endDate
        );
    };

    private renderFilters(): ReactNode {
        const { filters, isScrolled, selectedPreset } = this.state;

        const hasChanges = this.hasChanges();
        const datePresets = ["4W", "3M", "6M", "Y"];

        return (
            <div
                className={isScrolled ? "bg-primary-transparent" : ""}
                style={{
                    position: "sticky",
                    top: 56,
                    zIndex: 100,
                    backgroundColor: isScrolled ? undefined : "transparent",
                    paddingTop: "12px",
                    paddingBottom: "8px",
                    marginBottom: "2px",
                    marginLeft: "-15px",
                    marginRight: "-15px",
                    paddingLeft: "15px",
                    paddingRight: "15px",
                    width: "calc(100% + 30px)",
                    transition: "background-color 0.2s ease"
                }}
            >
                <Row className="align-items-start">
                    <Col md={2}>
                        <Form.Group>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                                <Form.Label style={{ marginBottom: 0 }}>Start Date</Form.Label>
                                <div style={{ display: "flex", gap: "4px" }}>
                                    {datePresets.map(preset => (
                                        <Button
                                            key={preset}
                                            size="sm"
                                            variant={selectedPreset === preset ? "secondary" : "outline-secondary"}
                                            onClick={() => this.handleDatePreset(preset)}
                                            style={{
                                                padding: "0px 5px",
                                                fontSize: "10px",
                                                lineHeight: "1.2",
                                                height: "20px",
                                                minWidth: "20px",
                                                ...(selectedPreset !== preset ? {
                                                    backgroundColor: "#e9ecef",
                                                    borderColor: "#ced4da",
                                                    color: "#6c757d"
                                                } : {})
                                            }}
                                            title={
                                                preset === "4W" ? "Last 4 Weeks" :
                                                preset === "3M" ? "Last 3 Months" :
                                                preset === "6M" ? "Last 6 Months" :
                                                "Last Year"
                                            }
                                        >
                                            {preset}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                            <Form.Control
                                type="date"
                                value={filters.startDate}
                                max={new Date(Date.now() - 86400000).toISOString().split("T")[0]}
                                onChange={(e) => this.handleDateChange("startDate", e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2}>
                        <Form.Group>
                            <Form.Label style={{ marginBottom: "4px" }}>End Date</Form.Label>
                            <div title="Date selection is only possible for a full day's data.">
                                <Form.Control
                                    type="date"
                                    value={filters.endDate}
                                    max={new Date(Date.now() - 86400000).toISOString().split("T")[0]}
                                    onChange={(e) => this.handleDateChange("endDate", e.target.value)}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                    <Col md={2}>
                        <Form.Group>
                            <Form.Label style={{ marginBottom: "4px" }}>&nbsp;</Form.Label>
                            <Button
                                variant={hasChanges ? "secondary" : "outline-secondary"}
                                onClick={this.handleApplyFilters}
                                disabled={!hasChanges}
                                style={{
                                    width: "100%",
                                    display: "block",
                                    ...(!hasChanges && {
                                        backgroundColor: "#e9ecef",
                                        borderColor: "#ced4da",
                                        color: "#6c757d",
                                        cursor: "not-allowed"
                                    })
                                }}
                            >
                                Apply Filters
                            </Button>
                        </Form.Group>
                    </Col>
                </Row>
            </div>
        );
    }

    render(): ReactNode {
        const { isLoading, error, isDarkTheme, sidebarOpen, sidebarEntries } = this.state;

        return (
            <div ref={this.containerRef} style={{ display: "flex", alignItems: "flex-start", backgroundColor: isDarkTheme ? "#060606" : "#ffffff", color: isDarkTheme ? "#ffffff" : "#000000", minHeight: "100vh" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Container className="mw-100" key="weekly-executive-summary" style={{ position: "relative" }}>
                    <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                        <ExportButton
                            context={this.getExportContext()}
                            enabled={!isLoading && !error && this.state.analyticsData.length > 0}
                            scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                            onScheduleClick={() => this.setState({ showScheduleModal: true })}
                        />
                        <Button
                            onClick={this.toggleSidebar}
                            style={{
                                backgroundColor: "transparent",
                                border: "none",
                                fontSize: "20px",
                                padding: "4px 8px",
                                color: isDarkTheme ? "#ffffff" : "#000000"
                            }}
                            title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                        >
                            <BookHalf />
                        </Button>
                        <Button
                            onClick={this.toggleTheme}
                            style={{
                                backgroundColor: "transparent",
                                border: "none",
                                fontSize: "24px",
                                padding: "4px 8px",
                                color: isDarkTheme ? "#ffffff" : "#000000"
                            }}
                            title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                        >
                            {isDarkTheme ? <Sun /> : <Moon />}
                        </Button>
                    </div>

                    <Row>
                        <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                            <h2 style={{ color: isDarkTheme ? "#ffffff" : "#000000" }}>Weekly Executive Summary</h2>
                        </Col>
                    </Row>

                    {this.renderFilters()}
                    <PopPeriodBanner startDate={this.state.appliedFilters.startDate} endDate={this.state.appliedFilters.endDate} />

                    <Row style={{ paddingTop: "24px" }}>
                        <Col md={12}>
                            {isLoading && (
                                <div className="text-center" style={{ padding: "40px" }}>
                                    <Spinner animation="border" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </Spinner>
                                </div>
                            )}

                            {error && (
                                <Alert variant="danger">{error}</Alert>
                            )}

                            {!isLoading && !error && (
                                <>
                                    {this.renderSummaryBlocks()}
                                    {this.renderChart()}
                                </>
                            )}
                        </Col>
                    </Row>

                    <ScheduleExportModal
                        show={this.state.showScheduleModal}
                        onHide={() => this.setState({ showScheduleModal: false })}
                        filters={this.convertToAnalyticsFilters()}
                        pageKey="weekly_executive_summary"
                        pageTitle="Weekly Executive Summary"
                        customerSlug={this.props.customerConfig.slug}
                        editingSchedule={null}
                        onSave={this.handleScheduleSave}
                    />
                </Container>
              </div>
              <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
            </div>
        );
    }
}

export default WeeklyExecutiveSummary;
